<template>
  <div class="report-categories-container">
    <h1>{{ $t('report_categories.title') }}</h1>

    <el-row :gutter="20" class="action-row">
      <el-col :span="12">
        <el-input
          v-model="searchTerm"
          :placeholder="$t('report_categories.search')"
          prefix-icon="el-icon-search"
          clearable
          @clear="clearSearch">
        </el-input>
      </el-col>
      <el-col :span="12" style="text-align: right">
        <div class="action-buttons">
          <!-- Tenant selector for superadmins -->
          <el-select
            v-if="isSuperAdmin"
            v-model="selectedTenantId"
            filterable
            clearable
            :placeholder="$t('report_categories.select_tenant')"
            @change="onTenantChange"
            class="tenant-selector">
            <el-option
              v-for="tenant in tenants"
              :key="tenant.id"
              :label="tenant.name"
              :value="tenant.id">
            </el-option>
          </el-select>

          <el-button
            type="primary"
            icon="el-icon-plus"
            @click="showCreateDialog">
            {{ $t('report_categories.create') }}
          </el-button>
          <el-button
            icon="el-icon-refresh"
            :loading="loading"
            @click="loadReportCategories">
            {{ $t('report_categories.refresh') }}
          </el-button>
        </div>
      </el-col>
    </el-row>

    <!-- Categories table -->
    <el-table
      v-loading="loading"
      :data="filteredCategories"
      style="width: 100%"
      border>
      <el-table-column
        prop="title"
        :label="$t('report_categories.name')"
        min-width="150">
      </el-table-column>

      <el-table-column
        prop="type"
        :label="$t('report_categories.type')"
        width="120">
        <template slot-scope="scope">
          <el-tag :type="getCategoryTypeTag(scope.row.type)">
            {{ scope.row.type }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column
        prop="level"
        :label="$t('report_categories.level')"
        width="120">
        <template slot-scope="scope">
          <el-tag :type="getCategoryLevelTag(scope.row.level)">
            {{ scope.row.level }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column
        prop="color"
        :label="$t('report_categories.color')"
        width="100">
        <template slot-scope="scope">
          <div class="color-preview" :style="{ backgroundColor: scope.row.color }"></div>
          <span>{{ scope.row.color }}</span>
        </template>
      </el-table-column>

      <el-table-column
        prop="priority"
        :label="$t('report_categories.priority')"
        width="100">
      </el-table-column>

      <el-table-column
        :label="$t('report_categories.visibility_duration')"
        width="150">
        <template slot-scope="scope">
          {{ formatDuration(scope.row.visibilityDurationInMillis) }}
        </template>
      </el-table-column>

      <el-table-column
        :label="$t('settings.tenantName')"
        width="150">
        <template slot-scope="scope">
          {{ scope.row.tenant.name }}
        </template>
      </el-table-column>

      <!-- Actions column -->
      <el-table-column
        :label="$t('report_categories.actions')"
        width="150"
        fixed="right">
        <template slot-scope="scope">
          <el-button
            type="primary"
            icon="el-icon-edit"
            size="mini"
            circle
            @click="editCategory(scope.row)">
          </el-button>
          <el-button
            type="danger"
            icon="el-icon-delete"
            size="mini"
            circle
            @click="confirmDeleteCategory(scope.row)">
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- Create/Edit Category Dialog -->
    <el-dialog
      :title="isEditing ? $t('report_categories.edit') : $t('report_categories.create')"
      :visible.sync="dialogVisible"
      width="50%">
      <report-category-form
        v-if="dialogVisible"
        :category="currentCategory"
        :is-super-admin="isSuperAdmin"
        :tenants="tenants"
        :default-tenant-id="selectedTenantId"
        @submit="onCategorySubmit"
        @cancel="dialogVisible = false">
      </report-category-form>
    </el-dialog>

    <!-- Delete Confirmation Dialog -->
    <el-dialog
      :title="$t('report_categories.delete_confirm_title')"
      :visible.sync="deleteDialogVisible"
      width="30%">
      <div v-html="deleteConfirmationMessage"></div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="deleteDialogVisible = false">
          {{ $t('base.cancel') }}
        </el-button>
        <el-button type="danger" @click="deleteCategory">
          {{ $t('report_categories.delete') }}
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import {reportCategoryService} from '@/services/ReportCategoryService.class';
import ReportCategoryForm from './ReportCategoryForm.vue';
import ReportCategory from '@/model/ReportCategory';

const authStateMappers = createNamespacedHelpers('auth');
const tenantStateMappers = createNamespacedHelpers('tenant');

export default {
  name: 'ReportCategories',
  components: {
    ReportCategoryForm
  },
  data() {
    return {
      categories: [],
      searchTerm: '',
      loading: false,
      dialogVisible: false,
      deleteDialogVisible: false,
      isEditing: false,
      currentCategory: null,
      categoryToDelete: null,
      selectedTenantId: null
    };
  },
  computed: {
    ...authStateMappers.mapGetters({
      isSuperAdmin: 'isSuperAdmin',
      isAdmin: 'isAdmin'
    }),
    ...tenantStateMappers.mapGetters({
      tenants: 'getTenants',
      currentTenant: 'getSelectedTenant'
    }),
    filteredCategories() {
      if (!this.searchTerm) {
        return this.categories;
      }

      const searchLower = this.searchTerm.toLowerCase();
      return this.categories.filter(category =>
        category.title.toLowerCase().includes(searchLower) ||
        category.type.toLowerCase().includes(searchLower) ||
        category.level.toLowerCase().includes(searchLower)
      );
    },
    deleteConfirmationMessage() {
      if (!this.categoryToDelete) {
        return '';
      }

      const reportCount = this.categoryToDelete.reportsAssignedCount || 0;

      if (reportCount > 0) {
        return this.$t('report_categories.delete_confirm_content_with_reports', {
          count: `<span style="color: #f56c6c; font-weight: bold; font-size: 16px;">${reportCount}</span>`
        });
      } else {
        return this.$t('report_categories.delete_confirm_content_no_reports');
      }
    },
  },
  mounted() {
    if (this.isSuperAdmin) {
      this.loadTenants();
    } else if (this.currentTenant) {
      this.selectedTenantId = this.currentTenant.id;
    }
    this.loadReportCategories();
  },
  methods: {
    ...tenantStateMappers.mapActions(['getTenants']),

    loadTenants() {
      if (this.isSuperAdmin) {
        this.getTenants();
      }
    },

    loadReportCategories() {
      this.loading = true;
      reportCategoryService.getAll(this.selectedTenantId)
        .then(response => {
          this.categories = response.data.map(item => ReportCategory.fromJson(item));
        })
        .catch(error => {
          console.error('Error loading report categories:', error);
          this.$message.error(this.$t('report_categories.load_error'));
        })
        .finally(() => {
          this.loading = false;
        });
    },

    onTenantChange() {
      this.loadReportCategories();
    },

    clearSearch() {
      this.searchTerm = '';
    },

    showCreateDialog() {
      this.isEditing = false;
      this.currentCategory = new ReportCategory();

      // Set tenant ID for non-superadmins
      if (!this.isSuperAdmin && this.currentTenant) {
        this.currentCategory.tenantId = this.currentTenant.id;
      } else if (this.selectedTenantId) {
        this.currentCategory.tenantId = this.selectedTenantId;
      }

      this.dialogVisible = true;
    },

    editCategory(category) {
      this.isEditing = true;
      this.currentCategory = new ReportCategory(
        category.id,
        category.title,
        category.color,
        category.colorCSS,
        category.priority,
        category.visibilityDurationInMillis,
        category.type,
        category.level,
        category.tenantId
      );
      this.dialogVisible = true;
    },

    onCategorySubmit(category) {
      if (this.isEditing) {
        // Update existing category
        reportCategoryService.update(category.id, category)
          .then(() => {
            this.$message.success(this.$t('report_categories.update_success'));
            this.dialogVisible = false;
            this.loadReportCategories();
          })
          .catch(error => {
            console.error('Error updating category:', error);
            this.$message.error(this.$t('report_categories.update_error'));
          });
      } else {
        // Create new category
        reportCategoryService.create(category)
          .then(() => {
            this.$message.success(this.$t('report_categories.create_success'));
            this.dialogVisible = false;
            this.loadReportCategories();
          })
          .catch(error => {
            console.error('Error creating category:', error);
            this.$message.error(this.$t('report_categories.create_error'));
          });
      }
    },

    confirmDeleteCategory(category) {
      this.categoryToDelete = category;
      this.deleteDialogVisible = true;
    },

    deleteCategory() {
      if (this.categoryToDelete) {
        reportCategoryService.delete(this.categoryToDelete.id)
          .then(() => {
            this.$message.success(this.$t('report_categories.delete_success'));
            this.deleteDialogVisible = false;
            this.loadReportCategories();
          })
          .catch(error => {
            console.error(error);
            if (error.response.status === 400) {
              this.$message.error(this.$t('report_categories.delete_conflict_error'));
            } else {
              this.$message.error(this.$t('report_categories.delete_error'));
            }
          });
      }
    },

    getCategoryTypeTag(type) {
      switch (type) {
        case 'MAINTENANCE': return 'info';
        case 'INFO': return '';
        case 'WARNING': return 'warning';
        case 'ERROR': return 'danger';
        default: return '';
      }
    },

    getCategoryLevelTag(level) {
      switch (level) {
        case 'CREATE': return '';
        case 'UPDATE': return 'warning';
        case 'FINISH': return 'success';
        default: return '';
      }
    },

    formatDuration(milliseconds) {
      const hours = Math.floor(milliseconds / (1000 * 60 * 60));
      const days = Math.floor(hours / 24);

      if (days > 0) {
        return `${days} ${this.$t('report_categories.days')}`;
      } else {
        return `${hours} ${this.$t('report_categories.hours')}`;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.report-categories-container {
  padding: 0 20px;

  h1 {
    margin-bottom: 20px;
  }

  .action-row {
    margin-bottom: 20px;

    .action-buttons {
      display: flex;
      justify-content: flex-end;
      gap: 10px;

      .tenant-selector {
        min-width: 200px;
      }
    }
  }

  .color-preview {
    display: inline-block;
    width: 20px;
    height: 20px;
    border-radius: 4px;
    vertical-align: middle;
    margin-right: 8px;
    border: 1px solid #dcdfe6;
  }
}
</style>
